import React, { useMemo } from 'react';
import { severitiesStyle, SEVERITY_LABELS } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '../../../utils/filterUtils';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  getPolicyViolationsByAssetType,
  getPolicyViolationsByPolicyName,
  getPolicyViolationsBySeverity,
} from '../PolicyViolations/queries';
import { filtersFieldToIgnore } from '../ticket.types';
import { IEntityViewChartsProps } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper } from './utils';

const PolicyViolationCharts: React.FC<IEntityViewChartsProps> = ({ filters: f, isExpanded = true, setIsExpanded, searchFields }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const { fieldTypeMap } = useAvContext().accountEntities;
  const filteredFilters = Object.keys(f).reduce(
    (acc, field) =>
      !filtersFieldToIgnore.includes(field) && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    {}
  );

  const regularFilters = filterToExpression(fieldTypeMap, filteredFilters);
  const customFilters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: f?.search?.[0],
    fieldTypeMap,
  });

  const widgetsToShow = [
    getPolicyViolationsBySeverity({ filter: customFilters }),
    getPolicyViolationsByPolicyName({ filter: customFilters, isSmallScreen }),
    getPolicyViolationsByAssetType({ filter: customFilters, isSmallScreen }),
  ];

  const { data: topPVBySeverity, isLoading: topPVBySeverityLoading } = useQueryObject({
    queryObject: widgetsToShow[0].requests[0],
    options: { placeholderData: keepPreviousData, gcTime: 0 },
  });
  const { data: topPVBYName, isLoading: topViolationsByNameLoading } = useQueryObject({
    queryObject: widgetsToShow[1].requests[0],
    options: { placeholderData: keepPreviousData, gcTime: 0 },
  });

  const { data: topPVByType, isLoading: topPVByTypeLoading } = useQueryObject({
    queryObject: widgetsToShow[2].requests[0],
    options: { placeholderData: keepPreviousData, enabled: !isSmallScreen, gcTime: 0 },
  });

  const previewWidgetsSummerized = [
    getPolicyViolationsByPolicyName({ filter: customFilters, isSmallScreen, size: 1, groupOthers: false }),
    getPolicyViolationsByAssetType({ filter: customFilters, isSmallScreen, size: 2, groupOthers: false }),
  ];

  const { data: overviewTopPVBYName, isLoading: overviewTopViolationsByNameLoading } = useQueryObject({
    queryObject: previewWidgetsSummerized[0].requests[0],
    options: { placeholderData: keepPreviousData, gcTime: 0 },
  });

  const { data: overviewTopPVByType, isLoading: overviewTopPVByTypeLoading } = useQueryObject({
    queryObject: previewWidgetsSummerized[1].requests[0],
    options: { placeholderData: keepPreviousData, enabled: !isSmallScreen, gcTime: 0 },
  });

  const summarizeOnSuccess = (data, dataKey, titleKey) =>
    data?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr[titleKey]]: curr[dataKey],
      }),
      {}
    );

  const summaryItems = useMemo(
    () => [
      {
        title: 'Policy violations by severity',
        data: topPVBySeverity,
        isLoading: topPVBySeverityLoading,
        showPills: true,
        getPillsStyleFunc: key => ({ ...severitiesStyle.label, ...(severitiesStyle[key]?.label || {}) }),
        getLabelFunc: key => SEVERITY_LABELS[key],
        onSuccess: data =>
          summarizeOnSuccess(data, widgetsToShow[0].requests[0].select.metrics[0].name, widgetsToShow[0].requests[0].select.dims[0].name),
      },
      {
        title: 'Top policy violations by name',
        data: overviewTopPVBYName,
        isLoading: overviewTopViolationsByNameLoading,
        onSuccess: data =>
          summarizeOnSuccess(
            data,
            previewWidgetsSummerized[0].requests[0].select.metrics[0].name,
            previewWidgetsSummerized[0].requests[0].select.dims[0].name
          ),
      },
      {
        title: 'Top policy violations type',
        data: overviewTopPVByType,
        isLoading: overviewTopPVByTypeLoading,
        showIcon: false,
        onSuccess: data =>
          summarizeOnSuccess(
            data,
            previewWidgetsSummerized[1].requests[0].select.metrics[0].name,
            previewWidgetsSummerized[1].requests[0].select.dims[0].name
          ),
      },
    ],
    [topPVByType, topPVBYName, topViolationsByNameLoading, topPVBySeverity, topPVBySeverityLoading, topPVByTypeLoading]
  );

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: summaryItems }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          {widgetsToShow.map(widget => (
            <Box
              key={widget.definition.title.title}
              sx={{
                ...entityViewWidgetStyle,
                position: 'relative',
                backgroundColor: theme => theme.palette.white.main,
              }}>
              <DraggableWidget
                widget={widget}
                customToolbar={
                  <Typography sx={{ whiteSpace: 'nowrap' }} variant="h6">
                    {widget.definition.title.title}
                  </Typography>
                }>
                {renderWidget({ widget })}
              </DraggableWidget>
            </Box>
          ))}
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default PolicyViolationCharts;
